export interface IPdfPageDimensions {
  pdfWidth: number;
  pdfHeight: number;
  containerWidth: number;
  containerHeight: number;
  pageWidthScale: number;
  pageFitScale: number;
}

export const LANDSCAPE_SCALE_FACTOR = 0.90;
export const PORTRAIT_SCALE_FACTOR = 0.65;
// Only genuinely wide slides (16:10/16:9 decks, aspect ≥ 1.5) get the fit-to-viewport
// rule. Milder landscape-ish documents (e.g. letter rotated, ~1.29) stay on the
// near-square path and keep their prior behavior.
export const LANDSCAPE_ASPECT_THRESHOLD = 1.5;
export const NEAR_SQUARE_MIN_ASPECT = 0.8;
export const MOBILE_BREAKPOINT_PX = 768;
export const MOBILE_MIN_READABLE_RATIO = 0.7;
export const MOBILE_NEAR_SQUARE_FIT_RATIO = 0.85;
export const MIN_PAGE_WIDTH_SCALE = 0;

export function calculatePdfScale(
  viewportWidth: number,
  viewportHeight: number,
  pdfWidth: number,
  pdfHeight: number,
  // pdf.js 'page-fit' scale — the scale at which the whole page fits the container,
  // expressed in the same CSS-pixel units pdf.js actually lays pages out in. Landscape
  // scaling must derive from this (not from raw PDF points) to avoid the points→CSS-px
  // (×96/72) unit mismatch that made landscape slides overflow vertically.
  pageFitScale?: number,
  logger?: { log: (message: string, properties?: Record<string, any>) => void }
): number {
  const aspectRatio = pdfWidth / pdfHeight;
  const isLandscape = aspectRatio >= LANDSCAPE_ASPECT_THRESHOLD;
  const isNearSquare = aspectRatio >= NEAR_SQUARE_MIN_ASPECT && !isLandscape;
  const isMobile = viewportWidth < MOBILE_BREAKPOINT_PX;

  const widthScale = (viewportWidth / pdfWidth) * PORTRAIT_SCALE_FACTOR;
  const heightScale = (viewportHeight / pdfHeight) * LANDSCAPE_SCALE_FACTOR;
  const isViewportPortrait = viewportHeight > viewportWidth;
  const portraitFactor = isViewportPortrait ? 0.55 : 0.50;

  const mode = isLandscape ? 'landscape' : isNearSquare ? 'near-square' : 'portrait';
  const device = isMobile ? 'mobile' : 'desktop';

  logger?.log('[PDF Scale] mode resolution', {
    mode, device, pdfAspect: +aspectRatio.toFixed(3),
    container: `${viewportWidth}×${viewportHeight}`,
    pdf: `${pdfWidth.toFixed(0)}×${pdfHeight.toFixed(0)}`,
    widthScale: +widthScale.toFixed(4), heightScale: +heightScale.toFixed(4),
  });

  if (isMobile) {
    if (isLandscape) {
      // Fit the whole slide to the viewport (both axes) via pdf.js page-fit, ×margin,
      // so the entire landscape slide is visible without scrolling.
      const useFit = !!pageFitScale && pageFitScale > 0;
      const chosen = useFit ? pageFitScale! * LANDSCAPE_SCALE_FACTOR : heightScale;
      logger?.log('[PDF Scale] chosen', { path: 'mobile-landscape', basis: useFit ? 'pageFitScale' : 'heightScale', pageFitScale, chosen: +chosen.toFixed(4) });
      return chosen;
    }
    if (isNearSquare) {
      const wouldOverflow = pdfWidth * heightScale > viewportWidth;
      const isReadable = heightScale >= widthScale * MOBILE_NEAR_SQUARE_FIT_RATIO;
      const chosen = !wouldOverflow && isReadable ? heightScale : widthScale;
      logger?.log('[PDF Scale] chosen', { path: 'mobile-near-square', wouldOverflow, isReadable, chosen: +chosen.toFixed(4), basis: !wouldOverflow && isReadable ? 'heightScale' : 'widthScale' });
      return chosen;
    }
    const portraitChosen = (viewportWidth / pdfWidth) * portraitFactor;
    logger?.log('[PDF Scale] chosen', { path: 'mobile-portrait', isViewportPortrait, portraitFactor, chosen: +portraitChosen.toFixed(4) });
    return portraitChosen;
  }

  if (isLandscape) {
    // Fit the whole slide to the viewport (both axes) via pdf.js page-fit, ×margin,
    // so the entire landscape slide — including its footer — is visible without scrolling.
    const useFit = !!pageFitScale && pageFitScale > 0;
    const chosen = useFit ? pageFitScale! * LANDSCAPE_SCALE_FACTOR : heightScale;
    logger?.log('[PDF Scale] chosen', { path: 'desktop-landscape', basis: useFit ? 'pageFitScale' : 'heightScale', pageFitScale, chosen: +chosen.toFixed(4) });
    return chosen;
  }
  if (isNearSquare) {
    const wouldOverflow = pdfWidth * heightScale > viewportWidth;
    if (!wouldOverflow) {
      logger?.log('[PDF Scale] chosen', { path: 'desktop-near-square', wouldOverflow: false, chosen: +heightScale.toFixed(4), basis: 'heightScale' });
      return heightScale;
    }
    logger?.log('[PDF Scale] chosen', { path: 'desktop-near-square', wouldOverflow: true, fallback: +widthScale.toFixed(4), basis: 'widthScale' });
  }
  // const heightLimitedScale = (viewportHeight / pdfHeight) * LANDSCAPE_SCALE_FACTOR;
  // const chosen = Math.min(widthScale, heightLimitedScale);
  const chosen = (viewportWidth / pdfWidth) * portraitFactor;
  logger?.log('[PDF Scale] chosen', { path: 'desktop-portrait', isViewportPortrait, portraitFactor, chosen: +chosen.toFixed(4) });
  return chosen;
}
